'use client';

import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';

import { useRouter } from 'src/routes/hooks';
import { paths, newSearchParams } from 'src/routes/paths';

import { useTranslate } from 'src/locales';
import { FooterDesktop } from 'src/layouts/main/footer';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { AccountTitle, AccountStatistics } from 'src/components/account-data';

import { useAuthContext } from 'src/auth/hooks';
import { SignInButton } from 'src/auth/components/sign-in-button';
import { SignOutButton } from 'src/auth/components/sign-out-button';

import { UpgradeBlock } from './nav-upgrade';
import { ThemeButton } from './theme-button';
import { AccountButton } from './account-button';
import { LanguagePopover } from './language-popover';
import { NotificationsDrawer } from './notifications-drawer';

// ----------------------------------------------------------------------

export function AccountDrawer({ sx, layoutQuery = 'md', ...other }) {
  const router = useRouter();

  const { t, currentLang } = useTranslate();

  const { user, authenticated } = useAuthContext();

  const search = newSearchParams({ country: currentLang.countryName });

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();

  const handleRedirectTo = (path) => {
    onClose();
    router.push(path);
  };

  const renderAvatar = () => (
    <Avatar
      src={user?.photoURL}
      alt={user?.displayName}
      onClick={() => handleRedirectTo(`${paths.user.account}${search}`)}
      sx={[
        (theme) => ({
          top: -40,
          cursor: 'pointer',
          position: 'absolute',
          width: { xs: 92, md: 88 },
          height: { xs: 92, md: 88 },
          border: `solid 2px ${theme.palette.common.white}`,
        }),
      ]}
    />
  );

  const MENU_USER = [
    {
      label: 'Profile',
      icon: 'fluent-color:scan-person-24',
      href: `${paths.user.root}${search}`,
    },
    {
      label: 'Favorites',
      icon: 'fluent-color:heart-24',
      href: `${paths.shop}${newSearchParams({ country: currentLang.countryName, list: 'favorites' })}`,
    },
    {
      label: 'Purchases',
      icon: 'fluent-color:apps-list-20',
      href: `${paths.orders.purchases}${search}`,
    },
    {
      label: 'Wallet',
      icon: 'fluent-color:savings-24',
      href: `${paths.user.wallet}${search}`,
    },
  ];

  const renderList = () =>
    MENU_USER.map((option) => (
      <MenuItem
        key={option.label}
        onClick={() => handleRedirectTo(option.href)}
        sx={{
          '&:hover': { color: 'text.primary' },
          mb: { xs: '0 !important', [layoutQuery]: '8px !important' },
        }}
      >
        <Iconify icon={option.icon} width={22} />

        <Box component="span" sx={{ ml: 2 }}>
          {t(option.label)}
        </Box>
      </MenuItem>
    ));

  const MENU_SHOP = [
    {
      label: 'Dashboard',
      icon: 'fluent-color:poll-24',
      href: `${paths.analytics}${search}`,
    },
    {
      label: 'Promotions',
      icon: 'fluent-color:megaphone-loud-24',
      href: `${paths.promotions}${search}`,
    },
    {
      label: 'Coupons',
      icon: 'fluent-color:gift-card-24',
      href: `${paths.coupons}${search}`,
    },
    {
      label: 'Sales',
      icon: 'fluent-color:apps-list-detail-32',
      href: `${paths.orders.sales}${search}`,
    },
    {
      label: 'Dispatch orders',
      icon: 'fluent-color:clipboard-task-24',
      href: `${paths.orders.dispatch}${search}`,
    },
    {
      label: 'Collaborators',
      icon: 'fluent-color:people-24',
      href: `${paths.user.collaborators}${search}`,
    },
  ];

  const renderMenu = () =>
    MENU_SHOP.map((option) => (
      <MenuItem
        key={option.label}
        onClick={() => handleRedirectTo(option.href)}
        sx={{
          '&:hover': { color: 'text.primary' },
          mb: { xs: '0 !important', [layoutQuery]: '8px !important' },
        }}
      >
        <Iconify icon={option.icon} width={22} />

        <Box component="span" sx={{ ml: 2 }}>
          {t(option.label)}
        </Box>
      </MenuItem>
    ));

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={user?.photoURL}
        displayName={user?.displayName}
        sx={[...(Array.isArray(sx) ? sx : [sx])]}
        {...other}
      />

      <SwipeableDrawer
        open={open}
        anchor="right"
        onOpen={onOpen}
        disableDiscovery
        onClose={onClose}
        disableSwipeToOpen
        disableBackdropTransition
        slotProps={{ paper: { sx: { width: { xs: 1, md: 370 } } } }}
      >
        <Box
          sx={{
            pb: 1,
            width: 1,
            display: 'flex',
            alignItems: 'center',
            pt: { xs: 2, md: 1 },
            px: { xs: 2.5, md: 1.5 },
            bgcolor: 'background.neutral',
            justifyContent: 'space-between',
          }}
        >
          <IconButton onClick={onClose} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>

          {/** @slot Language popover */}
          <LanguagePopover sx={{ display: { md: 'none' } }} />

          {/** @slot Notifications drawer */}
          <NotificationsDrawer
            onCloseAccountDrawer={onClose}
            sx={{ py: 0, display: { md: 'none' } }}
          />
        </Box>

        <Scrollbar>
          <Box sx={{ p: 3, pt: 6, bgcolor: 'background.neutral' }}>
            <Card
              sx={{
                p: 3,
                pt: 9,
                gap: 3,
                display: 'flex',
                overflow: 'visible',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              {renderAvatar()}

              <Box>
                <AccountTitle
                  user={user}
                  justifyContent="center"
                  slotProps={{
                    iconify: { width: 14 },
                    title: { sx: { typography: 'subtitle1' } },
                  }}
                />

                <Box sx={{ display: 'grid' }}>
                  <Typography
                    noWrap
                    variant="body2"
                    component="div"
                    sx={{ color: 'text.secondary', textAlign: 'center' }}
                  >
                    {user?.email}
                  </Typography>
                </Box>

                {user?.id && (
                  <AccountStatistics
                    justifyContent="center"
                    totalSales={user?.totalSales}
                    totalRating={user?.totalRating}
                    totalFollowers={user?.totalFollowers}
                  />
                )}
              </Box>

              {authenticated ? (
                <SignOutButton onClose={onClose} />
              ) : (
                <SignInButton onClose={onClose} />
              )}
            </Card>
          </Box>

          <Stack
            sx={[
              (theme) => ({
                p: 2,
                borderTop: `dashed 1px ${theme.vars.palette.divider}`,
                borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
              }),
            ]}
          >
            <Typography component="div" variant="overline" sx={{ p: 1, pb: { xs: 0, md: 1.5 } }}>
              {t('Account')}
            </Typography>

            {renderList()}

            <Typography component="div" variant="overline" sx={{ p: 1, pb: { xs: 0, md: 1.5 } }}>
              {t('Shop')}
            </Typography>

            {renderMenu()}
          </Stack>

          <UpgradeBlock
            sx={[
              (theme) => ({
                borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
              }),
            ]}
          />

          <FooterDesktop
            layoutQuery={layoutQuery}
            sx={{ bgcolor: 'transparent', display: { [layoutQuery]: 'none' } }}
          />
        </Scrollbar>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            justifyContent: 'space-between',
            display: { xs: 'flex', md: 'none' },
          }}
        >
          {/** @slot Theme button */}
          <ThemeButton />

          <IconButton onClick={onClose}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Box>
      </SwipeableDrawer>
    </>
  );
}
